/**
 * useWakeLock — Acquires a Screen Wake Lock while a retreat is running.
 *
 * Without this, iOS and Android will turn off the screen during a multi-hour
 * retreat. When the screen sleeps, the AudioContext is suspended by the OS and
 * bells stop firing. AudioContextWatcher polls and tries to re-resume, but iOS
 * requires a new user gesture for that — the watcher can't save you.
 *
 * Wake Lock prevents the screen from sleeping, keeping the AudioContext alive.
 *
 * Behaviour:
 * - Acquired when isRunning becomes true
 * - Released when isRunning becomes false (retreat ends / app backgrounds)
 * - Re-acquired on visibilitychange (document re-enters foreground) if still running,
 *   because the OS automatically releases the lock when the page hides
 */
import { useCallback, useEffect, useRef } from "react";
import { createChildLogger } from "@retreat/shared";

const log = createChildLogger("useWakeLock");

export function useWakeLock(isRunning: boolean): void {
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  const acquire = useCallback(async () => {
    if (!("wakeLock" in navigator)) return;
    if (sentinelRef.current && !sentinelRef.current.released) return;
    try {
      sentinelRef.current = await navigator.wakeLock.request("screen");
      log.info("Screen Wake Lock acquired");
      sentinelRef.current.addEventListener("release", () => {
        log.info("Screen Wake Lock released by OS");
      });
    } catch (err) {
      // NotAllowedError fires if the document is not visible — safe to ignore
      log.info("Wake Lock request failed (page not visible or unsupported)", { error: String(err) });
    }
  }, []); // sentinelRef is a stable ref — no deps needed

  const release = useCallback(() => {
    if (sentinelRef.current && !sentinelRef.current.released) {
      sentinelRef.current.release().catch(() => {});
      sentinelRef.current = null;
      log.info("Screen Wake Lock released by app");
    }
  }, []); // same

  useEffect(() => {
    if (isRunning) {
      void acquire();
    } else {
      release();
    }
    return () => { release(); };
  }, [isRunning, acquire, release]);

  // Re-acquire after the tab comes back to foreground (OS releases lock on hide)
  useEffect(() => {
    if (!isRunning) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void acquire();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isRunning, acquire]);
}
